import { NavLink, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Wrench, CarFront, UserSearch, FileWarning } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasAnyRole, ROLE_OPPSLAG_PREMIUM } from '../../utils/roles'

/**
 * One Oppslag page, four tabs.
 *
 * The two premium tabs are hidden — not merely disabled — for a user without
 * oppslag_premium_user, because a visible tab that always 403s reads as a bug.
 * The backend enforces the same split independently; this is presentation, not
 * security.
 *
 * No shared filter bar here, unlike BruktbilLayout: each tab takes a different
 * kind of input (a plate list, a single vehicle, a person) and they share
 * nothing worth lifting.
 */
export default function OppslagLayout() {
  const { t } = useTranslation('oppslag')
  const { user } = useAuth()
  const premium = hasAnyRole(user, [ROLE_OPPSLAG_PREMIUM])

  const tabs = [
    { label: t('layout.tabs.teknisk'), path: 'teknisk', icon: Wrench, vis: true },
    { label: t('layout.tabs.kjoretoy'), path: 'kjoretoy', icon: CarFront, vis: premium },
    { label: t('layout.tabs.person'), path: 'person', icon: UserSearch, vis: premium },
    // Premium: a lien names a private individual and the debt collector
    // pursuing them. Public register data, but not the anonymous technical
    // data the basic role exists for.
    { label: t('layout.tabs.heftelser'), path: 'heftelser', icon: FileWarning, vis: premium },
  ].filter((tab) => tab.vis)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('layout.title')}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('layout.intro')}
        </p>
      </div>

      {/* Underline tabs — same treatment as Bruktbil and Leads */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 gap-1 overflow-x-auto">
        {tabs.map((tab) => (
          <NavLink
            key={tab.path}
            to={`/oppslag/${tab.path}`}
            className={({ isActive }) =>
              `px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`
            }
          >
            <span className="flex items-center gap-2">
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </span>
          </NavLink>
        ))}
      </div>

      <Outlet />
    </div>
  )
}
